/**
 * Expanded detail for classified Outlook (Microsoft Graph) mail feed events
 * (`payload.kind === "graph_mail"`). Shows sender context and a decluttered
 * email body; classification metadata stays in a compact footer.
 */
import { Mail } from "lucide-react";

import { SensitiveText } from "@/components/common/Sensitive";
import { formatDateTimePlain } from "@/lib/weekDates";
import type { FeedEffectPayload, GraphMailPayload } from "@/types/feed";
import { GraphMailBody } from "./GraphMailBody";
import { graphMailCategoryLabel } from "./feedVisuals";
import {
  canLoadGraphMailBody,
  graphMailMailboxId,
  graphMailMessageId,
} from "./graphMailRefs";

/** Type guard: does this payload carry a classified Graph mail message? */
export function isGraphMailPayload(
  payload: FeedEffectPayload,
): payload is GraphMailPayload {
  return (payload as { kind?: string })?.kind === "graph_mail";
}

function MetaRow({
  label,
  value,
  mask = false,
}: {
  label: string;
  value: string;
  mask?: boolean;
}) {
  return (
    <div className="flex gap-2 text-[11px]">
      <span className="w-16 shrink-0 text-muted-foreground">{label}</span>
      <span className="min-w-0 flex-1 break-words text-muted-foreground">
        {mask ? <SensitiveText value={value} /> : value}
      </span>
    </div>
  );
}

export function GraphMailFeedDetail({ payload }: { payload: GraphMailPayload }) {
  const mailboxId = graphMailMailboxId(payload);
  const messageId = graphMailMessageId(payload);
  const showBody = mailboxId && messageId && canLoadGraphMailBody(payload);

  return (
    <div className="flex flex-col gap-2.5 rounded-lg bg-white/5 p-3">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          <Mail className="size-3" />
          {graphMailCategoryLabel(payload.category)}
        </span>
        {payload.received_at && (
          <span className="text-[11px] tabular-nums text-muted-foreground">
            {formatDateTimePlain(payload.received_at)}
          </span>
        )}
      </div>

      {payload.subject && (
        <p className="text-sm font-medium leading-snug break-words">
          {payload.subject}
        </p>
      )}

      {showBody ? (
        <GraphMailBody mailboxId={mailboxId} messageId={messageId} />
      ) : (
        <p className="text-xs text-muted-foreground">
          Email body unavailable for this message.
        </p>
      )}

      <div className="mt-0.5 flex flex-col gap-1 border-t border-border/40 pt-2">
        <MetaRow
          label="Mailbox"
          value={payload.mailbox_email}
          mask
        />
        {payload.booking_source_id && (
          <MetaRow label="Booking" value={payload.booking_source_id} />
        )}
        {payload.rule_id && <MetaRow label="Rule" value={payload.rule_id} />}
        {payload.confidence && (
          <MetaRow label="Confidence" value={payload.confidence} />
        )}
      </div>
    </div>
  );
}
